import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, User, Menu, X, ChevronDown, Heart, Search, CircleX } from "lucide-react";
import { useEffect, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import API, { getImageUrl } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import { useLanguage } from "@/contexts/LanguageContext";

const BRAND_LOGO = getImageUrl("naura-numa/assets/frontend/logo-haniyah-gallery.jpg");

export default function Header() {
  const { user, logout } = useAuth();
  const { locale, t } = useLanguage();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasBillingAccess, setHasBillingAccess] = useState(false);

  const navLinks = [
    { label: locale === "id" ? "Semua Produk" : "All Products", href: "/products" },
    { label: locale === "id" ? "Katalog PO" : "Preorder Catalog", href: "/products?stock_type=preorder" },
    { label: locale === "id" ? "Katalog Ready" : "Ready Stock Catalog", href: "/products?stock_type=ready_stock" },
    { label: locale === "id" ? "Brand" : "Brands", href: "/brands" },
  ];

  const submitSearch = (q) => {
    const query = q.trim();
    const params = location.pathname === "/products"
      ? new URLSearchParams(location.search)
      : new URLSearchParams();

    if (query) params.set("search", query);
    else params.delete("search");

    const nextSearch = params.toString();
    const nextUrl = nextSearch ? `/products?${nextSearch}` : "/products";
    const currentUrl = `${location.pathname}${location.search}`;

    if (currentUrl !== nextUrl) {
      navigate(nextUrl, { replace: true });
    }

    setMobileOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    submitSearch(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get("search") || "";

    if (location.pathname === "/products") {
      setSearchQuery(prev => (prev === urlQuery ? prev : urlQuery));
      return;
    }

    setSearchQuery(prev => (prev === "" ? prev : ""));
  }, [location.pathname, location.search]);

  useEffect(() => {
    const q = searchQuery.trim();

    if (location.pathname !== "/products") return;

    const params = new URLSearchParams(location.search);
    const hasSearchParam = params.has("search");

    if (q.length >= 2) {
      const timer = setTimeout(() => submitSearch(q), 350);
      return () => clearTimeout(timer);
    }

    if (q.length === 0 && hasSearchParam) {
      const timer = setTimeout(() => submitSearch(""), 150);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, location.pathname, location.search]);

  useEffect(() => {
    const allowedRoles = ["admin", "reseller", "agen", "distributor", "produsen", "mitra"];
    if (!user || !allowedRoles.includes(user.role)) {
      setHasBillingAccess(false);
      return;
    }
    API.get("/billing/summary")
      .then(() => setHasBillingAccess(true))
      .catch(() => setHasBillingAccess(false));
  }, [user]);

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-stone-200/50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between min-h-16 py-2 md:py-0 gap-2 sm:gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <button className="md:hidden p-2 -ml-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
              <img src={BRAND_LOGO} alt="Haniyah Gallery" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover border border-stone-200 bg-white" />
              <span className="font-serif text-lg sm:text-2xl md:text-3xl font-medium tracking-tight leading-none text-stone-900 whitespace-nowrap">
                Haniyah Gallery
              </span>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <Link key={l.href} to={l.href} className="font-serif text-base md:text-lg text-stone-600 hover:text-primary transition-colors tracking-wider">
                {l.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md items-center">
            <div className="relative w-full">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(e);
                  }
                }}
                placeholder={t("common.search_product_brand")}
                className="w-full pl-10 pr-10 py-2 text-sm border border-stone-200 bg-white focus:outline-none focus:border-primary"
                data-testid="header-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                  aria-label="Clear search"
                  data-testid="header-search-clear"
                >
                  <CircleX className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>

          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {user && (
              <>
                <NotificationBell />
                <Link to="/wishlist" className="text-stone-600 hover:text-stone-900 transition-colors" data-testid="wishlist-link">
                  <Heart className="w-5 h-5" />
                </Link>
                <Link to="/cart" className="relative" data-testid="cart-link">
                  <ShoppingBag className="w-5 h-5 text-stone-600 hover:text-stone-900 transition-colors" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900" data-testid="user-menu-trigger">
                  <User className="w-5 h-5" />
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-stone-500 uppercase">{user.role}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="my-profile-link">Profil Saya</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="my-wishlist-link">{t("nav.my_wishlist")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="my-orders-link">{t("nav.my_orders")}</DropdownMenuItem>
                  {hasBillingAccess && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="my-billing-link">Tagihan Saya</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/billing?view=waiting")} data-testid="my-process-link">Proses</DropdownMenuItem>
                    </>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-link">{t("nav.admin_dashboard")}</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="font-serif text-base md:text-lg text-stone-600 hover:text-primary transition-colors tracking-wider" data-testid="login-link">
                {t("nav.sign_in")}
              </Link>
            )}

          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-stone-200/50 fade-in space-y-4">
            <form onSubmit={handleSearch} className="relative">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(e);
                  }
                }}
                placeholder={t("common.search_product_brand")}
                className="w-full pl-10 pr-10 py-2 text-sm border border-stone-200 bg-white focus:outline-none focus:border-primary"
                data-testid="mobile-header-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                  aria-label="Clear search"
                  data-testid="mobile-header-search-clear"
                >
                  <CircleX className="w-4 h-4" />
                </button>
              )}
            </form>
            {navLinks.map((l) => (
              <Link key={l.href} to={l.href} onClick={() => setMobileOpen(false)} className="block py-3 font-serif text-lg text-stone-600 hover:text-primary tracking-wider border-b border-stone-100 last:border-0">
                {l.label}
              </Link>
            ))}
            {user && (
              <>
                <Link to="/profile" onClick={() => setMobileOpen(false)} className="block py-3 font-serif text-lg text-stone-600 hover:text-primary tracking-wider border-b border-stone-100">Profil Saya</Link>
                <Link to="/orders" onClick={() => setMobileOpen(false)} className="block py-3 font-serif text-lg text-stone-600 hover:text-primary tracking-wider border-b border-stone-100">{t("nav.my_orders")}</Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
